import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import mixpanel from 'mixpanel-browser';

// Firebase
import { db } from '../config/firebase-config';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, setDoc, deleteDoc, query, where, onSnapshot, orderBy, startAfter, limit } from 'firebase/firestore';

// Redux
import { connect } from 'react-redux';

// Actions
import { toggleSideNav, setPage, add_To_Recent_History } from '../actions/navActions';
import { getMyReviews, setPosts, set_ShowBottomSpinner } from '../actions/postActions';

// Layout Wrapper
import Layout from '../components/layout/Layout';

// components
import Spinner from '../components/common/Spinner';
import Review from '../components/Reviews/Review';

const MyReviewsPage = ({ 
    getMyReviews, 
    set_ShowBottomSpinner,
    toggleSideNav, 
    setPage, 
    add_To_Recent_History,
    auth, 
    post: { 
        posts, 
        no_more_posts,
        loading,
        last_page_doc,
        show_bottom_spinner
    }, 
    nav: { 
        sideNav,
        website
    }
}) => {

    // Window width
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // State variable to track if Mixpanel event has been sent
    const [sentMixpanel, setSentMixpanel] = useState(false);

    // Posts
    const [postList, setPostList] = useState([]);
    const [gotPosts, setGotPosts] = useState(false);

    // Scroll direction - for displaying top nav bar
    const [scrollDirection, setScrollDirection] = useState("up");
    const [oldScrollY, setOldScrollY] = useState(window.scrollY);

    // Temporarily disable to load more function to avoid duplicate fetch requests
    const [canLoadMore, setCanLoadMore] = useState(true);

    // Get the current URL and extract the "redirect" parameter
    const url_filter = (window.location.href);
    const url = new URL(url_filter);
    const hostname = window.location.hostname;

    // Determine window size breakpoints
    const isMobile = windowWidth <= 769;
    const isTablet = windowWidth <= 1000;

    // Listen for window resize event and update window width state
    useEffect(() => {

        // Add event listener for window resize
        window.addEventListener('resize', () => handleWindowSizeChange());

        // Clean up by removing event listener on component unmount
        return () => window.removeEventListener('resize', () => handleWindowSizeChange());
    }, []);

    useEffect(() => {

        // Set the current page
        setPage('my reviews');
        add_To_Recent_History(url.pathname);  // current url
    }, []);

    useEffect(() => {
        if(auth.loading) return;
        
        // Fetch liked posts when the authenticated user changes
        if(auth.user) {
            setGotPosts(false);
            handleGetReviews();
        } else {
            setGotPosts(true); // allow guest users to view this page (shows empty list)
        }

    }, [auth.user, auth.loading]);

    // Render posts on screen
    useEffect(() => {

        renderPostList();

    }, [posts, canLoadMore, gotPosts]);

    // Update window width state on window resize
    const handleWindowSizeChange = () => {
        setWindowWidth(window.innerWidth);
    };


    // Infinite scroll AND handle top nav bar display
    const handleScroll = (e) => {
        const { offsetHeight, scrollTop, scrollHeight} = e.target

        const newScrollY = offsetHeight + scrollTop;

        // --- Handle top nav bar display
        if(isMobile) {
            if(oldScrollY < newScrollY){

                // scrolling down - hide top nav
                setScrollDirection("down");

                if(document.getElementById('feed-header') !== null) {
                    document.getElementById('feed-header').classList.remove("active");
                }
                
            } else {

                // scrolling up - show top nav
                setScrollDirection("up");

                if(document.getElementById('feed-header') !== null) {
                    document.getElementById('feed-header').classList.add("active");
                }
            }
        }
        // --- END Handle top nav bar display

        // update the scroll position for future comparison
        setOldScrollY(newScrollY)

        // Check if the user has scrolled to the bottom of the content and conditions for loading more posts are met
        if (newScrollY >= scrollHeight - 1 && !show_bottom_spinner && !no_more_posts && posts.length > 0) {
            loadMore(last_page_doc);
        }
    }

    // Function to load more paginated posts
    const loadMore = async (lastPage_doc) => {
        try {
            // Temporarily disable to load more function to avoid duplicate fetch requests
            setCanLoadMore(false);

            // Show spinner
            set_ShowBottomSpinner(true);

            await getMyReviews({ user_id: auth.user._id, lastPageDoc: lastPage_doc });
            
        } catch (error) {
            console.log("ERROR loading more", error);
        } finally {
            // Re-enable the load more capabilty
            setCanLoadMore(true);
        }
    }

    const handleGetReviews = () => {

        if(auth.user?._id) {
            // Load initial posts
            getMyReviews({ user_id: auth.user._id });
            setGotPosts(true);
        }
    }

    const renderPostList = async () => {
        setPostList([]);

        if((posts === null && loading) || !gotPosts || auth.loading) {
            setPostList([(
                <div className="no-rides">
                    <Spinner />
                </div>
            )])
        }
        else { 
            if(posts?.length > 0) {
                posts.map(post_doc => {
                    const renderPostRecursive = (postComments, parentPost) => {
                    
                        const lastComment = postComments[postComments.length - 1]; // Get the last comment
    
                        return (
                            <Fragment>
                                <Review key={lastComment._id} postDoc={lastComment} parentPost={parentPost} isComment />
    
                                {/* Recursive call to render comments of comments */}
                                {lastComment.commentsOfComments && lastComment.commentsOfComments.length > 0 && (
                                    <div className="comments-of-comments">
                                    {renderPostRecursive(lastComment.commentsOfComments, lastComment)}
                                    </div>
                                )}
                            </Fragment>
                        );
    
                    };
    
                    if(post_doc.commentsOfComments && post_doc.commentsOfComments.length > 0) {
                        setPostList(postList => [...postList, (
                            <div className='post_container'>
                                <Review key={post_doc._id} postDoc={post_doc} />
    
                                {renderPostRecursive(post_doc.commentsOfComments, post_doc)}
                            </div>
                        )])
                    } else {
                        setPostList(postList => [...postList, (
                            <Review key={post_doc._id} postDoc={post_doc} isComment={post_doc.type === 'comment'} />
                        )])
                    }
                }) 
            } else {

                setPostList(postList => [...postList, (
                    <div className="no-posts-container">
                        <div className="no-chats">
                            <h1>You haven’t left any reviews yet</h1>
                            <p className='placeholder_text'>When you write a review, it’ll show up here.</p>
                        </div>
                    </div>
                )])
                
            }
        } 

        setGotPosts(true);
    }

    // Function to handle Mixpanel analytics
    const handleMixpanel = () => {
        mixpanel.init(process.env.REACT_APP_MIXPANEL_ID);
        mixpanel.identify(auth.user._id);
        mixpanel.track("My Reviews Page View");
    }

    // Trigger Mixpanel analytics on initial render in production mode
    if(process.env.NODE_ENV === 'production' && !sentMixpanel && auth.user !== null && !hostname.includes('xbuysell')) {
        handleMixpanel();
        setSentMixpanel(true);
    }

    return (
        <Layout handleScroll={handleScroll} sideNav={sideNav} handleSlideMenu={toggleSideNav} page="my reviews">

            {/* List of generated posts */}
            {postList}

            {/* Render a spinner at the bottom if there are more posts to load */}
            {show_bottom_spinner && !no_more_posts && <Spinner />}
            
            {/* Display a message when there are no more posts */}
            <div className="no-rides">{(no_more_posts && posts.length !== 0) && "You've reached the end."}</div>
        </Layout>
    )
}

MyReviewsPage.propTypes = {
    getMyReviews: PropTypes.func.isRequired,
    set_ShowBottomSpinner: PropTypes.func.isRequired,
    toggleSideNav: PropTypes.func.isRequired,
    setPage: PropTypes.func.isRequired, // Function prop to set the current page in the navigation
    add_To_Recent_History: PropTypes.func.isRequired,
    post: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired, // Object prop representing the authentication state of the user
    nav: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({

    // Mapping the states from the Redux store to the below props
    post: state.post,
    auth: state.auth,
    nav: state.nav
});

export default connect(mapStateToProps, { 

    // Connecting actions to the component
    getMyReviews, 
    set_ShowBottomSpinner,
    toggleSideNav, 
    setPage,
    add_To_Recent_History
})(MyReviewsPage);
